import { trigger } from '@angular/animations';
import { AsyncPipe, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, NavigationStart, Params, Router, RouterLink } from '@angular/router';
import { AssetDto, GalleryDto, GalleryFeedbackOptions, Section } from '@nusign/picflow-backend-types';
import { Subscription, firstValueFrom } from 'rxjs';
import { fadeDownIfSubtle } from '../../../animations/fade-down-subtle.if.animation';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { assetLoadSettings } from '../../../consts';
import { AssetTransitionerComponent } from '../../../gallery-shared/components/asset-transitioner/asset-transitioner.component';
import { LayoutAssetComponent } from '../../../gallery-shared/components/layout-asset/layout-asset.component';
import { VideoViewerComponent } from '../../../gallery-shared/components/video-viewer/video-viewer.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { singleFilter } from '../../../gallery-shared/lib/filter-assets';
import { CanDisplayAssetPipe } from '../../../gallery-shared/pipes/can-display-asset.pipe';
import { CanDownloadAssetPipe } from '../../../gallery-shared/pipes/can-download-asset.pipe';
import { GalleryItem } from '../../../gallery/classes/gallery-item.class';
import { AssetDownloadButtonComponent } from '../../../gallery/components/asset-download-button/asset-download-button.component';
import { getAssetSectionHeight } from '../../../gallery/lib/get-asset-section-height';
import { sortAssetSection } from '../../../gallery/lib/sort-asset-section';
import { AssetTransitionService } from '../../../gallery/services/asset-transition.service';
import { LayoutGridService } from '../../../gallery/services/layout-grid.service';
import { LayoutMosaicColumnsService } from '../../../gallery/services/layout-mosaic-columns.service';
import { LayoutMosaicRowsService } from '../../../gallery/services/layout-mosaic-rows.service';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { debounce } from '../../../lib/debounce';
import { sleep } from '../../../lib/sleep';
import { AssetSupportsTransparencyPipe } from '../../../pipes/asset-supports-transparency.pipe';
import { AssetPreGenerateService } from '../../../services/asset-pre-generate.service';
import { AssetService } from '../../../services/asset.service';
import { BrowserService } from '../../../services/browser.service';
import { StorageService } from '../../../services/storage.service';
import { UpdateService } from '../../../services/update.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { AssetUrlPipe } from '../../../shared/pipes/asset-url.pipe';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';

@Component({
  selector: 'app-assets-section',
  templateUrl: './assets-section.component.html',
  styleUrls: ['./assets-section.component.scss'],
  standalone: true,
  providers: [AssetUrlPipe],
  animations: [trigger('assetDownloadButton', fadeDownIfSubtle)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    AssetUrlPipe,
    AsyncPipe,
    SvgComponent,
    VideoViewerComponent,
    AssetVersionUrlPipe,
    CanDisplayAssetPipe,
    AssetSupportsTransparencyPipe,
    LayoutAssetComponent,
    ButtonDirective,
    AssetDownloadButtonComponent,
    CanDownloadAssetPipe,
    InferredHoverDirective,
  ],
})
export class AssetsSectionComponent implements OnInit, OnDestroy, OnChanges {
  private platformId = inject(PLATFORM_ID);
  private layoutGrid = inject(LayoutGridService);
  private layoutRows = inject(LayoutMosaicRowsService);
  private layoutColumns = inject(LayoutMosaicColumnsService);
  private assetService = inject(AssetService);
  private assetUrl = inject(AssetUrlPipe);
  private router = inject(Router);
  private updateService = inject(UpdateService);
  private browserService = inject(BrowserService);
  private assetPreGenerateService = inject(AssetPreGenerateService);
  private assetTransitionService = inject(AssetTransitionService);
  private storage = inject(StorageService);
  private route = inject(ActivatedRoute);
  private check = inject(ChangeDetectorRef);
  @Input() section: Section;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  @Input() approvedAssetCount: number;
  @Input() filters: Filter[] = [];
  @Input() blockEmbedAssetView: boolean;
  @Input() darkBackground: boolean;
  @Input() allowedAndEnabledFeedbackOptions: GalleryFeedbackOptions[];
  @ViewChild('assetGallery') assetGallery: ElementRef;
  @ViewChild('assetTransitioner') assetTransitioner: AssetTransitionerComponent;
  items: GalleryItem[];
  allItems: GalleryItem[];
  visibleItems: GalleryItem[];
  sectionHeight: number;
  inView: { [key: string]: boolean } = {};
  sectionWidth: number;
  updateSub: Subscription;
  routerSub: Subscription;
  assetPage: boolean;
  queryParams: Params;
  hoverItem: GalleryItem;
  isDesktop = ScreenSizes.screenSize() === 'desktop';
  hoverContextOpenItem: GalleryItem;

  @HostListener('window:scroll', ['$event']) windowScroll() {
    this.calcInView();
  }

  @HostListener('window:resize', ['$event']) winResize() {
    this.calculateAllItemPositions();
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';
  }

  async ngOnInit() {
    this.sectionWidth = this.section.width;
    this.getAssets();

    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.assetPage = this.browserService.urlIsAssetUrl(event.url);
        this.calcInView();
      }
    });

    this.updateSub = this.updateService.onAssetUpdate.subscribe(event => {
      if (!event) return;
      if (!this.allItems) return;
      if (event.asset.section !== this.section.id) return;
      // if (event.name === 'asset.created') {
      //   if (event.asset.version_data['status'] !== 'completed') return
      //   this.allItems.push(new GalleryItem(event.asset));
      // }
      if (event.name === 'asset.deleted') {
        const index = this.allItems.findIndex(item => item.asset.id === event.asset.id);
        if (index !== -1) this.allItems.splice(index, 1);
      }
      if (event.name === 'asset.updated') {
        const index = this.allItems.findIndex(item => item.asset.id === event.asset.id);
        if (index !== -1) Object.assign(this.allItems[index].asset, event.asset);
        else this.allItems.push(new GalleryItem(event.asset)); // This can happen if the asset goes from being rejected to not rejected
      }
      this.sortAssets();
      this.filterAssets();
    });
  }

  private sortAssets() {
    this.allItems = sortAssetSection(
      this.allItems.map(i => i.asset),
      this.section.sorting
    ).map(asset => new GalleryItem(asset));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters) {
      this.filterAssets();
      const filters = this.filters.length ? JSON.stringify(this.filters) : undefined;
      this.queryParams = { filters, autoplay: true };
    }
    this.check.detectChanges();
  }

  setHoverContextOpenItem(item: GalleryItem, contextItem: GalleryItem) {
    const currentIsContextItem = this.hoverContextOpenItem?.asset.id === contextItem.asset.id;
    if (!item && !currentIsContextItem) return;

    this.hoverContextOpenItem = item;
    this.check.detectChanges();
  }

  private filterAssets() {
    if (!this.allItems) return;
    if (this.accessLevel === 'view_only') {
      this.items = this.allItems.filter(i => i.asset.status !== 'rejected');
    } else {
      this.items = this.allItems.filter(item => !this.filters.find(filter => singleFilter(filter, item.asset)));
    }

    this.calculateAllItemPositions();
  }

  private async getAssets() {
    if (isPlatformServer(this.platformId)) return;
    const assets = await firstValueFrom(this.assetService.getAssetsObservable(this.section.id, this.gallery.id));
    this.allItems = assets.map(asset => new GalleryItem(asset));
    this.sortAssets();
    this.filterAssets();
    this.calcInView();
  }

  private internalCalcItemPos(items: GalleryItem[]) {
    if (this.section.grid_layout === 'grid') {
      return this.layoutGrid.calculateAllItemPositions(this.section, items, 1);
    }
    if (this.section.grid_layout === 'rows') {
      return this.layoutRows.calculateAllItemPositions(this.section, items, 1);
    }
    if (this.section.grid_layout === 'columns') {
      return this.layoutColumns.calculateAllItemPositions(this.section, items, 1);
    }
    console.error('Unsupported layout', this.section.grid_layout);
  }

  calculateAllItemPositions() {
    if (!this.items?.length) return (this.sectionHeight = 0);
    const width = this.internalCalcItemPos(this.items);
    if (width) {
      if (width < this.section.width) this.sectionWidth = width;
      else this.sectionWidth = this.section.width;
    }
    this.sectionHeight = getAssetSectionHeight(this.items, this.section.spacing, this.section.show_filenames);
    this.calcInView();
  }

  async calcInView() {
    if (this.assetPage) return;
    await sleep(0);
    if (!this.items) return;

    this.visibleItems = [];
    const preGenerateItems: GalleryItem[] = [];
    const sectionBox = this.assetGallery.nativeElement.getBoundingClientRect?.() || { top: 0 };
    this.items.forEach(item => {
      const top = sectionBox.top + item.dimensions.y;
      const bottom = sectionBox.top + item.dimensions.y + item.dimensions.height;
      if (
        this.isInView(
          top,
          bottom,
          assetLoadSettings.galleryScreensToPreloadBefore,
          assetLoadSettings.galleryScreensToPreloadAfter
        )
      )
        this.visibleItems.push(item);
      else if (
        this.isInView(
          top,
          bottom,
          assetLoadSettings.galleryScreensToPreloadBefore,
          assetLoadSettings.galleryScreensToPreloadAfter + assetLoadSettings.galleryScreensToPreGenerate
        )
      )
        preGenerateItems.push(item);
    });

    preGenerateItems.forEach(async item => {
      const url = await this.calcAssetUrl(item);
      if (url) this.assetPreGenerateService.preloadAsset(url);
    });

    this.pregenerateHighRes();

    this.check.detectChanges();
  }

  async calcAssetUrl(item: GalleryItem) {
    if (!canDisplayAsset(item.asset)) return;
    return await this.assetUrl.transform(item.asset, '', item.dimensions.width, true);
  }

  public pregenerateHighRes = debounce(() => {
    const onScreenItems = [];
    if (!this.assetGallery) return;
    const sectionBox = this.assetGallery.nativeElement.getBoundingClientRect();
    this.items.forEach(item => {
      const top = sectionBox.top + item.dimensions.y - 64;
      const bottom = sectionBox.top + item.dimensions.y + item.dimensions.height - 64;
      if (assetLoadSettings.preGenerateHighResFromList && this.isInView(top, bottom, 0, 0)) onScreenItems.push(item);
    });

    // Pre generating assets for asset page
    const sideMenuOpen = this.route.snapshot.fragment || this.storage.get('asset-sidebar');
    onScreenItems.forEach(async item => {
      const bounds = this.assetTransitionService.getAssetPageAssetBounds(item.asset, sideMenuOpen);
      const url = await this.assetUrl.transform(item.asset, '', bounds.width, false);
      this.assetPreGenerateService.preloadAsset(url);
    });
  }, 250);

  private allowDownload(asset: AssetDto) {
    if (this.accessLevel === 'edit') return true;
    if (!this.gallery.download_single_asset) return false;
    if (this.gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  }

  assetRightClick(asset: AssetDto, event: Event) {
    if (this.allowDownload(asset)) return true;
    event.preventDefault();
    event.stopPropagation();
  }

  private isInView(
    top: number,
    bottom: number,
    bufferBeforeMultiplier: number,
    bufferAfterMultiplier: number
  ): boolean {
    const windowHeight = typeof window === 'undefined' ? 1000 : window.innerHeight;
    const visibleWindowHeight = windowHeight - 64;
    const beforeBuffer = visibleWindowHeight * bufferBeforeMultiplier;
    const afterBuffer = visibleWindowHeight * bufferAfterMultiplier;
    if (bottom < -beforeBuffer) return false;
    if (top > visibleWindowHeight + afterBuffer) return false;
    return true;
  }

  assetNameClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  downloadClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  ngOnDestroy() {
    this.updateSub.unsubscribe();
    this.routerSub.unsubscribe();
  }
}
